import { getApiErrorMessage } from '../../utils/apiError';

/**
 * Operator-facing messages for supplier-order commands. Backend prose can
 * mention implementation details (FIFO or margin), so stable codes must be
 * translated here before they reach an OPERATIONS user.
 */
// *_LINE_REMOVAL_BLOCKED: refused by servers before WP12 B1 (a removed line
// is now withdrawn after a confirmation); kept so an older server still reads.
const SUPPLIER_ORDER_ERROR_CODE_KEYS: Record<string, string> = {
  SUPPLIER_ORDER_INVOICED_LINE_REMOVAL_BLOCKED: 'orders:supplierOrders.detail.invoicedLineRemovalBlocked',
  SUPPLIER_ORDER_INVOICED_DELETE_BLOCKED: 'orders:supplierOrders.detail.invoicedDeleteBlocked',
  // WP12 B1: received batches trace to the ZD — "Anuluj i ukryj" instead.
  SUPPLIER_ORDER_HAS_RECEIVED_STOCK: 'orders:supplierOrders.detail.receivedStockDeleteBlocked',
  SUPPLIER_ORDER_INVALID_STATUS_TRANSITION: 'orders:supplierOrders.detail.invalidStatusTransition',
  SUPPLIER_ORDER_PZ_REDUCTION_BLOCKED: 'orders:supplierOrders.detail.pzReductionBlocked',
  SUPPLIER_ORDER_PZ_LINE_REMOVAL_BLOCKED: 'orders:supplierOrders.detail.pzLineRemovalBlocked',
  // Inconsistent quantity pair (supplierOrderService createQuantityPairError):
  // points at the line's „Przelicznik jednostek” rounding. Not blocking.
  SUPPLIER_ORDER_QUANTITY_PAIR_INCONSISTENT: 'orders:supplierOrders.detail.quantityPairInconsistent',
  // Szkic ZD z potwierdzenia ZK (migration 197).
  SUPPLIER_ORDER_SUPPLIER_REQUIRED: 'orders:supplierOrders.drafts.errors.supplierRequired',
  SUPPLIER_ORDER_DRAFT_REJECT_ONLY: 'orders:supplierOrders.drafts.errors.rejectOnly',
  SUPPLIER_ORDER_DRAFT_REJECTED: 'orders:supplierOrders.drafts.errors.rejected',
  SUPPLIER_ORDER_DRAFT_NOT_PENDING: 'orders:supplierOrders.drafts.errors.notPending',
  SUPPLIER_ORDER_DRAFT_REJECTION_REASON_REQUIRED: 'orders:supplierOrders.drafts.errors.reasonRequired',
};

/**
 * Codes describing a state the operator must change before the command can
 * ever succeed (a line referenced by an approved invoice or received on a PZ).
 * Their toasts stay on screen until dismissed, like the PZ/WZ blocking codes.
 */
const SUPPLIER_ORDER_BLOCKING_CODES = new Set([
  'SUPPLIER_ORDER_INVOICED_LINE_REMOVAL_BLOCKED',
  'SUPPLIER_ORDER_INVOICED_DELETE_BLOCKED',
  'SUPPLIER_ORDER_HAS_RECEIVED_STOCK',
  'SUPPLIER_ORDER_INVALID_STATUS_TRANSITION',
  'SUPPLIER_ORDER_PZ_REDUCTION_BLOCKED',
  'SUPPLIER_ORDER_PZ_LINE_REMOVAL_BLOCKED',
  // Owner 2026-10-14: the server message names the invoices / WZ that keep
  // the ZD (Polish, no code key), with the "Anuluj i ukryj" offer.
  'SUPPLIER_ORDER_DELETE_BLOCKED',
]);

export function isBlockingSupplierOrderError(error: any): boolean {
  const code = error?.response?.data?.code;
  return typeof code === 'string' && SUPPLIER_ORDER_BLOCKING_CODES.has(code);
}

/** „faktura zakupu FZ 1/10, FZ 2/10; WZ … 3/10/2026” — kinds in server order. */
function formatDeleteBlockers(blockers: unknown, t: any): string {
  const groups = new Map<string, string[]>();
  for (const row of Array.isArray(blockers) ? blockers : []) {
    const kind = typeof row?.kind === 'string' ? row.kind : '';
    if (!kind) continue;
    if (!groups.has(kind)) groups.set(kind, []);
    if (row.number) groups.get(kind)!.push(String(row.number));
  }
  return Array.from(groups.entries())
    .map(([kind, numbers]) => `${t(`orders:supplierOrders.detail.deleteBlocker.${kind}`)}${numbers.length ? ` ${numbers.join(', ')}` : ''}`)
    .join('; ');
}

/**
 * The number is already the number (manual or auto) of another ZD — 400
 * SUPPLIER_ORDER_NUMBER_TAKEN. `supplierOrderId` comes only when the user may
 * open that ZD. The number modal shows it on the field (with a link).
 */
export interface SupplierOrderNumberTaken {
  number: string | null;
  supplierOrderId: string | null;
}

export function getSupplierOrderNumberTaken(error: unknown): SupplierOrderNumberTaken | null {
  const data = (error as { response?: { data?: { code?: unknown; number?: unknown; supplierOrderId?: unknown } } })?.response?.data;
  if (data?.code !== 'SUPPLIER_ORDER_NUMBER_TAKEN') return null;
  const number = typeof data.number === 'string' && data.number.trim() ? data.number.trim() : null;
  const supplierOrderId = typeof data.supplierOrderId === 'string' && data.supplierOrderId ? data.supplierOrderId : null;
  return { number, supplierOrderId };
}

/** Plain-text form (toasts, e.g. creating a ZD with a taken number). */
export function getSupplierOrderNumberTakenMessage(taken: SupplierOrderNumberTaken, t: any): string {
  return taken.number
    ? String(t('orders:supplierOrders.detail.numberTaken', { number: taken.number }))
    : String(t('orders:supplierOrders.detail.numberTakenUnnamed'));
}

export function getSupplierOrderErrorMessage(
  error: any,
  t: any,
  fallbackKey: string,
  fallbackParams?: Record<string, unknown>,
): string {
  const numberTaken = getSupplierOrderNumberTaken(error);
  if (numberTaken) return getSupplierOrderNumberTakenMessage(numberTaken, t);
  const code = error?.response?.data?.code;
  if (code === 'SUPPLIER_ORDER_DELETE_BLOCKED') {
    return t('orders:supplierOrders.detail.deleteBlocked', {
      ...fallbackParams,
      blockers: formatDeleteBlockers(error.response.data.details?.blockers, t),
    });
  }
  if (typeof code === 'string' && SUPPLIER_ORDER_ERROR_CODE_KEYS[code]) {
    return t(SUPPLIER_ORDER_ERROR_CODE_KEYS[code], fallbackParams);
  }
  return getApiErrorMessage(error, t, fallbackKey, undefined, fallbackParams);
}
